import { EChartsOption, format } from "echarts";
import { toUnicodeSubscript } from "entities/charts/eva/translators/utils";

import { getPrecision, getSeriesTooltipDot } from "components/charts/utils";

import getSeriesNameStrippingForecast from "./getSeriesNameStrippingForecast";

const tooltipPosition = (point, params, dom) => {
  const xPos = point[0];
  const yPos = point[1] - dom.offsetHeight > 48 ? point[1] - dom.offsetHeight : 48;
  const pos = [xPos, yPos]; //position top right of mouse cursor

  return pos;
};

const DEFAULT_PRECISION = 2;

const toAxisNumber = (x) => (typeof x === "number" ? x : new Date(x).getTime());

// Time axes would otherwise re-parse every date string on each pointer move.
const numericXCache = new WeakMap<unknown[], number[]>();
const getNumericX = (data): number[] => {
  let xs = numericXCache.get(data);
  if (!xs) {
    xs = data.map(([x]) => toAxisNumber(x));
    numericXCache.set(data, xs);
  }
  return xs;
};

// The two lowest and two highest distinct x values (cum repeats over shut-in months); a
// single x gives a step of zero.
const getEnds = (points: number[][]) => {
  let min = Infinity;
  let nextMin = Infinity;
  let max = -Infinity;
  let prevMax = -Infinity;
  points.forEach(([x]) => {
    if (x < min) [nextMin, min] = [min, x];
    else if (x > min && x < nextMin) nextMin = x;
    if (x > max) [prevMax, max] = [max, x];
    else if (x < max && x > prevMax) prevMax = x;
  });
  return {
    min,
    nextMin: Number.isFinite(nextMin) ? nextMin : min,
    max,
    prevMax: Number.isFinite(prevMax) ? prevMax : max
  };
};

/**
 * The y value of the highlighted product at the pointer. The hd-echarts axis trigger keeps
 * only the series nearest the cursor on each axis, so the product can be missing from the
 * formatter items; then its nearest point to that axis value is used.
 */
const getHighlightedYValue = (productSeries, formatterSeries) => {
  const items = Array.isArray(formatterSeries) ? formatterSeries : [formatterSeries];
  const names = productSeries.map((s) => s.name);

  const item = items.find((i) => names.includes(i?.seriesName));
  const itemY = Array.isArray(item?.value) ? item.value[1] : item?.value;
  if (itemY != null) return itemY;

  const xAxisIndex = productSeries[0].xAxisIndex ?? 0;
  const axisItem = items.find(
    (i) => i?.axisDim === "x" && (i.axisIndex ?? 0) === xAxisIndex
  );
  if (axisItem?.axisValue == null) return null;

  const pointerX = toAxisNumber(axisItem.axisValue);
  let nearest = null;
  let nearestDist = Infinity;
  productSeries.forEach((s) => {
    if (!s.data) return;
    const xs = getNumericX(s.data);
    const points = s.data.map(([, y], i) => [xs[i], y]).filter(([, y]) => y != null);
    if (!points.length) return;

    // More than one sample step past a series' own ends its nearest point is a stale
    // endpoint, not a value. Per series, because a backfit and its forecast share a name
    // with history between them.
    const { min, nextMin, max, prevMax } = getEnds(points);
    if (pointerX < min - (nextMin - min) || pointerX > max + (max - prevMax)) return;

    points.forEach(([x, y]) => {
      const dist = Math.abs(x - pointerX);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = y;
      }
    });
  });
  return nearest;
};

const formatValue = (value, yAxisTitle?: string) => {
  if (typeof value !== "number") return value;
  // Only a trailing "(unit)" is a unit; any other last word would be misread as one.
  const unit = yAxisTitle?.match(/\(([^()]+)\)\s*$/)?.[1];
  return value.toFixed(getPrecision(unit) ?? DEFAULT_PRECISION);
};

/**
 * "Gas", or "Gas (EVA Research)" when hovering that forecast; "Gas (Forecast)" without a
 * source. Escaped because forecast sources include user-named folders.
 */
const getProductLabel = (product: string, hoveredSeriesName?: string) => {
  const label = toUnicodeSubscript(product);
  if (!hoveredSeriesName || hoveredSeriesName === product)
    return format.encodeHTML(label);

  const source = hoveredSeriesName.match(/ Forecast\s*\((.+)\)\s*$/)?.[1];
  return format.encodeHTML(`${label} (${source ?? "Forecast"})`);
};

const tooltipFormatter = (
  currentlyHighlightedSeries: string,
  options,
  formatterSeries,
  hoveredSeriesName?: string
) => {
  const productSeries = (options.series ?? []).filter(
    (trace) => getSeriesNameStrippingForecast(trace.name) === currentlyHighlightedSeries
  );
  if (!productSeries.length) return;

  // A backfit shares its forecast's name (one source, drawn back and forward), so both count.
  const hoveredSeries = productSeries.filter((trace) => trace.name === hoveredSeriesName);
  const valueSeries = hoveredSeries.length ? hoveredSeries : productSeries;
  const [selectedSeries] = valueSeries;
  const value = getHighlightedYValue(valueSeries, formatterSeries);
  const yAxis = Array.isArray(options.yAxis)
    ? options.yAxis[selectedSeries.yAxisIndex ?? 0]
    : options.yAxis;

  const valueSpan =
    value == null
      ? ""
      : `:&emsp;<span style="font-weight:700;color:#666;font-family: var(--fontMono);">${formatValue(
          value,
          yAxis?.name
        )}</span>`;

  return `<div>${getSeriesTooltipDot(selectedSeries.itemStyle.color)}${getProductLabel(
    currentlyHighlightedSeries,
    hoveredSeries[0]?.name
  )}${valueSpan}</div>`;
};

/**
 * The tooltip triggers on axis, so the formatter receives several products per axis; the
 * highlighted product from the hover event picks which one to show (see getHighlightedYValue).
 * @param options Echarts chart options.
 * @param currentlyHighlightedSeries Derived from the hover event
 * @param hoveredSeriesName The exact series hovered; the value is read from the series of that name.
 * @returns
 */
const getOptionsWithTooltips = (
  options: EChartsOption,
  currentlyHighlightedSeries,
  hoveredSeriesName?: string
): EChartsOption => {
  const newOptions = {
    ...options,
    tooltip: {
      ...options.tooltip,
      position: tooltipPosition,
      formatter: (formatterSeries) =>
        tooltipFormatter(
          currentlyHighlightedSeries,
          options,
          formatterSeries,
          hoveredSeriesName
        )
    }
  };

  return newOptions;
};

export default getOptionsWithTooltips;
